/**
 * GerarVideoModal — Director IA + timeline multi-cena.
 *
 * Fluxo:
 *   1. Carrega avatares + vozes + templates do usuario
 *   2. Usuario edita texto fonte (resumo|completo|custom) E/OU clica
 *      "Otimizar com IA" → /videos/roteirizar (GPT-5.5) → cenas[]
 *   3. Timeline editavel: cada cena vira um SceneCard
 *      - Adicionar nova cena (fala/pausa/B-roll)
 *      - Reordenar (up/down)
 *      - Editar campos
 *      - Pra B-roll: gerar IA / upload / pular
 *   4. Salvar como template (config + cenas)
 *   5. Gerar → POST /videos com cenas[]
 *
 * Compat: se cenas[] vazio, manda texto simples (single-scene legado).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Sparkles, Loader2, Film, AlertCircle, Mic, Pause as PauseIcon,
  Image as ImageIcon, Save, ListTree, Wand2, Activity,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import {
  type Cena, type CenaFala, type CenaPausa, type CenaBroll,
  type RoteiroDirector, type VideoTemplate,
} from '../lib/cena';
import SceneCard from './SceneCard';

interface HeygenAvatar {
  avatar_id: string;
  avatar_name: string;
  preview_image_url: string | null;
  premium: boolean;
}

interface HeygenVoice {
  voice_id: string;
  name: string;
  language: string;
  gender: string | null;
  categoria?: 'minhas' | 'pt' | 'en' | 'outras';
}

interface VideoGerado {
  id: string;
  status: string;
}

interface Props {
  transcricaoId: string;
  textoCompleto: string;
  resumo: string | null;
  onClose: () => void;
}

type FonteTexto = 'resumo' | 'completo' | 'custom';

const HENRIQUE_AVATAR_ID = '4bde92acd94645199bfa9e678a932d1f';
// Henrique Cesar IVC (suporta pausas, ideal pra multi-cena)
const HENRIQUE_VOICE_ID = 'a1f99579b6ed48af959f1031548c05ce';

export default function GerarVideoModal({ transcricaoId, textoCompleto, resumo, onClose }: Props) {
  const nav = useNavigate();

  // Texto fonte
  const [fonteTexto, setFonteTexto] = useState<FonteTexto>(resumo ? 'resumo' : 'completo');
  const [textoCustom, setTextoCustom] = useState('');

  // Config base
  const [avatarId, setAvatarId] = useState(HENRIQUE_AVATAR_ID);
  const [voiceId, setVoiceId] = useState(HENRIQUE_VOICE_ID);
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('9:16');
  const [background, setBackground] = useState('#FFFFFF');

  // Timeline
  const [cenas, setCenas] = useState<Cena[]>([]);
  const [resumoEstrutura, setResumoEstrutura] = useState<string | null>(null);

  // Catalogos
  const [avatars, setAvatars] = useState<HeygenAvatar[]>([]);
  const [voices, setVoices] = useState<HeygenVoice[]>([]);
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);

  // Estados
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [otimizando, setOtimizando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Salvar template
  const [mostrandoSalvar, setMostrandoSalvar] = useState(false);
  const [nomeTemplate, setNomeTemplate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [a, v, t] = await Promise.all([
          api.get<{ avatars: HeygenAvatar[] }>('/heygen/avatars'),
          api.get<{ voices: HeygenVoice[] }>('/heygen/voices'),
          api.get<{ items: VideoTemplate[] }>('/videos/templates'),
        ]);
        const sorted = [...(a.avatars || [])].sort((x, y) => {
          const isCustomX = x.avatar_id === HENRIQUE_AVATAR_ID || x.avatar_name?.includes('Henrique');
          const isCustomY = y.avatar_id === HENRIQUE_AVATAR_ID || y.avatar_name?.includes('Henrique');
          if (isCustomX !== isCustomY) return isCustomX ? -1 : 1;
          return x.avatar_name.localeCompare(y.avatar_name);
        });
        setAvatars(sorted);
        setVoices(v.voices || []);
        setTemplates(t.items || []);
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : 'Falha ao carregar opções');
      } finally {
        setLoadingOpts(false);
      }
    })();
  }, []);

  const textoFinal = useMemo(() => {
    if (fonteTexto === 'resumo') return resumo || '';
    if (fonteTexto === 'completo') return textoCompleto;
    return textoCustom;
  }, [fonteTexto, resumo, textoCompleto, textoCustom]);

  // Estimativa: chars de fala (no HeyGen) + custos extras
  const stats = useMemo(() => {
    const usaCenas = cenas.length > 0;
    const charsFala = usaCenas
      ? cenas.filter((c) => c.tipo === 'fala').reduce((acc, c: any) => acc + (c.texto?.length || 0), 0)
      : textoFinal.length;
    // 880 chars/min é a média de fala PT-BR (~150 palavras/min × ~5.8 chars/palavra).
    // Mantenha alinhado com api/src/services/videos.service.ts:CHARS_POR_MIN
    const minutosFala = charsFala / 880;
    const custoHeygen = Math.round(minutosFala * 0.30 * 10000) / 10000;
    const custoBroll = usaCenas
      ? cenas.filter((c) => c.tipo === 'broll' && (c as CenaBroll).fonte === 'ia' && (c as CenaBroll).imagemAssetId).length * 0.04
      : 0;
    const custoTotal = custoHeygen + custoBroll;
    const duracaoFala = Math.round(minutosFala * 60);
    const duracaoExtra = usaCenas
      ? cenas.reduce((acc, c) => acc + (c.tipo === 'pausa' ? c.duracao : c.tipo === 'broll' ? c.duracao : 0), 0)
      : 0;
    return {
      charsFala,
      duracaoSeg: Math.round(duracaoFala + duracaoExtra),
      custoTotal,
      custoHeygen,
      custoBroll,
      cenasCount: cenas.length,
      brollPendente: cenas.filter((c) =>
        c.tipo === 'broll' && (c as CenaBroll).fonte !== 'pular' && !(c as CenaBroll).imagemAssetId
      ).length,
    };
  }, [cenas, textoFinal]);

  const validoSubmit = cenas.length > 0
    ? cenas.length <= 50 && stats.charsFala >= 5 && stats.brollPendente === 0
    : textoFinal.length >= 5 && textoFinal.length <= 5000;

  // ── Director IA ──────────────────────────────────────────────────────────
  async function otimizarComIA() {
    if (textoFinal.length < 10) {
      setErro('Texto muito curto para otimizar (min 10 chars)');
      return;
    }
    setOtimizando(true);
    setErro(null);
    try {
      const r = await api.post<RoteiroDirector>('/videos/roteirizar', { texto: textoFinal });
      setCenas(r.cenas);
      setResumoEstrutura(r.resumoEstrutura ?? null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao otimizar com IA');
    } finally {
      setOtimizando(false);
    }
  }

  /** Carrega cadencia detectada do Whisper e converte em cenas. */
  async function usarCadenciaOriginal() {
    setOtimizando(true);
    setErro(null);
    try {
      const r = await api.get<{ cenas: Cena[] }>(`/transcricoes/${transcricaoId}/cadencia`, { comoCenas: '1' });
      if (!r.cenas || r.cenas.length === 0) {
        setErro('Sem segmentos suficientes pra inferir cadência');
        return;
      }
      setCenas(r.cenas);
      setResumoEstrutura(`Cadência original: ${r.cenas.filter(c => c.tipo === 'fala').length} segmentos + ${r.cenas.filter(c => c.tipo === 'pausa').length} pausas detectadas no áudio`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao carregar cadência');
    } finally {
      setOtimizando(false);
    }
  }

  // ── Manipulacao de cenas ─────────────────────────────────────────────────
  function adicionarCena(tipo: 'fala' | 'pausa' | 'broll') {
    const nova: Cena =
      tipo === 'fala' ? { tipo: 'fala', texto: '', emotion: 'Friendly', speed: 1, pitch: 0, avatarStyle: 'normal' }
      : tipo === 'pausa' ? { tipo: 'pausa', duracao: 1.0 }
      : { tipo: 'broll', duracao: 3, imagemPrompt: '', fonte: 'ia' };
    setCenas([...cenas, nova]);
  }

  function atualizarCena(idx: number, c: Cena) {
    setCenas(cenas.map((x, i) => i === idx ? c : x));
  }

  function deletarCena(idx: number) {
    setCenas(cenas.filter((_, i) => i !== idx));
  }

  function moverCena(idx: number, delta: -1 | 1) {
    const novo = idx + delta;
    if (novo < 0 || novo >= cenas.length) return;
    const arr = [...cenas];
    [arr[idx], arr[novo]] = [arr[novo], arr[idx]];
    setCenas(arr);
  }

  function limparCenas() {
    if (cenas.length > 0 && !confirm('Limpar todas as cenas? Vai voltar pro modo simples.')) return;
    setCenas([]);
    setResumoEstrutura(null);
  }

  // ── Templates ────────────────────────────────────────────────────────────
  async function carregarTemplate(id: string) {
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setAvatarId(t.configJson.avatarId);
    setVoiceId(t.configJson.voiceId);
    setAspectRatio(t.configJson.aspectRatio);
    setBackground(t.configJson.background);
    setCenas(t.cenasJson || []);
  }

  async function salvarTemplate() {
    if (nomeTemplate.trim().length < 2) {
      setErro('Nome do template muito curto');
      return;
    }
    try {
      const novo = await api.post<VideoTemplate>('/videos/templates', {
        nome: nomeTemplate.trim(),
        config: { avatarId, voiceId, aspectRatio, background },
        cenas,
      });
      setTemplates([novo, ...templates]);
      setNomeTemplate('');
      setMostrandoSalvar(false);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao salvar template');
    }
  }

  async function deletarTemplate(id: string) {
    if (!confirm('Remover este template?')) return;
    try {
      await api.delete(`/videos/templates/${id}`);
      setTemplates(templates.filter((t) => t.id !== id));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao remover');
    }
  }

  // ── Submit ───────────────────────────────────────────────────────────────
  async function gerar() {
    if (!validoSubmit || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      const payload: any = {
        avatarId, voiceId, aspectRatio, background, transcricaoId,
      };
      if (cenas.length > 0) {
        payload.cenas = cenas;
        payload.texto = textoFinal || cenas.filter((c) => c.tipo === 'fala').map((c: any) => c.texto).join(' ');
      } else {
        payload.texto = textoFinal;
      }
      const v = await api.post<VideoGerado>('/videos', payload);
      onClose();
      nav(`/videos/${v.id}`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao gerar vídeo');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[92vh] flex flex-col">
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Film className="w-5 h-5 text-purple-600" />
            Gerar vídeo do avatar
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Templates */}
          {templates.length > 0 && (
            <section className="bg-gray-50 dark:bg-gray-900/40 rounded p-3 flex items-center gap-2 flex-wrap">
              <ListTree className="w-4 h-4 text-gray-500" />
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Template</span>
              <select
                onChange={(e) => carregarTemplate(e.target.value)}
                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded"
                defaultValue=""
              >
                <option value="">— carregar template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => deletarTemplate(t.id)}
                  className="text-xs text-gray-400 hover:text-red-600"
                  title={`Remover "${t.nome}"`}
                >
                  ✕ {t.nome}
                </button>
              ))}
            </section>
          )}

          {/* Texto fonte */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Texto fonte
            </h3>
            <div className="flex gap-2 mb-2">
              <FonteBtn ativo={fonteTexto === 'resumo'} onClick={() => setFonteTexto('resumo')} disabled={!resumo}>
                Resumo {!resumo && <span className="text-xs">(extraia primeiro)</span>}
              </FonteBtn>
              <FonteBtn ativo={fonteTexto === 'completo'} onClick={() => setFonteTexto('completo')}>
                Completo
              </FonteBtn>
              <FonteBtn ativo={fonteTexto === 'custom'} onClick={() => setFonteTexto('custom')}>
                Custom
              </FonteBtn>
            </div>
            {fonteTexto === 'custom' ? (
              <textarea
                value={textoCustom}
                onChange={(e) => setTextoCustom(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
                placeholder="Texto que o avatar vai falar..."
              />
            ) : (
              <div className="px-3 py-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded text-sm text-gray-700 dark:text-gray-300 max-h-32 overflow-y-auto">
                {textoFinal || <span className="italic text-gray-400">vazio</span>}
              </div>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                onClick={otimizarComIA}
                disabled={otimizando || textoFinal.length < 10}
                className="px-3 py-1.5 text-sm bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded disabled:opacity-50 flex items-center gap-1.5"
                title="Diretor de cena: GPT-5.5 marca pausas, entonação, B-roll"
              >
                {otimizando
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Wand2 className="w-4 h-4" />
                }
                Otimizar com IA
              </button>
              <button
                onClick={usarCadenciaOriginal}
                disabled={otimizando}
                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-1.5"
                title="Usa as pausas e velocidades detectadas no áudio original"
              >
                {otimizando
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Activity className="w-4 h-4" />
                }
                Usar cadência original
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                IA cria do zero · Cadência preserva o ritmo do interlocutor
              </p>
            </div>
            {resumoEstrutura && (
              <p className="text-xs italic text-purple-700 dark:text-purple-400 mt-2 bg-purple-50 dark:bg-purple-900/20 p-2 rounded">
                💡 {resumoEstrutura}
              </p>
            )}
          </section>

          {/* Avatar + Voz + Layout */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Avatar</h3>
              {loadingOpts ? (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
                </p>
              ) : (
                <select
                  value={avatarId}
                  onChange={(e) => setAvatarId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
                >
                  {avatars.slice(0, 200).map((a) => (
                    <option key={a.avatar_id} value={a.avatar_id}>
                      {a.avatar_name} {a.premium ? '(premium)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Voz</h3>
              {loadingOpts ? (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
                </p>
              ) : (
                <select
                  value={voiceId}
                  onChange={(e) => setVoiceId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
                >
                  {(['minhas', 'pt', 'en', 'outras'] as const).map((cat) => {
                    const grupo = voices.filter((v) => (v.categoria ?? 'outras') === cat);
                    if (grupo.length === 0) return null;
                    const label =
                      cat === 'minhas' ? '⭐ Minhas vozes (clones)'
                      : cat === 'pt' ? 'Português'
                      : cat === 'en' ? 'English'
                      : 'Outras';
                    return (
                      <optgroup key={cat} label={label}>
                        {grupo.map((v) => (
                          <option key={v.voice_id} value={v.voice_id}>
                            {v.name} ({v.gender || '?'})
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              )}
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Proporção</h3>
              <select
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
              >
                <option value="9:16">9:16 (Reels/Shorts)</option>
                <option value="16:9">16:9 (YouTube)</option>
                <option value="1:1">1:1 (Quadrado)</option>
              </select>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Fundo (cor padrão)</h3>
              <input
                type="color"
                value={background}
                onChange={(e) => setBackground(e.target.value)}
                className="w-full h-9 border border-gray-300 dark:border-gray-600 rounded"
              />
            </div>
          </section>

          {/* Timeline */}
          {cenas.length > 0 ? (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Timeline ({cenas.length}/50)
                </h3>
                <button
                  onClick={limparCenas}
                  className="text-xs text-gray-400 hover:text-red-600"
                >
                  limpar tudo
                </button>
              </div>
              <div className="space-y-2">
                {cenas.map((c, i) => (
                  <SceneCard
                    key={i}
                    cena={c}
                    index={i}
                    total={cenas.length}
                    aspectRatio={aspectRatio}
                    onChange={(nc) => atualizarCena(i, nc)}
                    onDelete={() => deletarCena(i)}
                    onMove={(d) => moverCena(i, d)}
                  />
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => adicionarCena('fala')}
                  className="px-2 py-1 text-xs border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-1"
                >
                  <Mic className="w-3 h-3" /> Fala
                </button>
                <button
                  onClick={() => adicionarCena('pausa')}
                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
                >
                  <PauseIcon className="w-3 h-3" /> Pausa
                </button>
                <button
                  onClick={() => adicionarCena('broll')}
                  className="px-2 py-1 text-xs border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-1"
                >
                  <ImageIcon className="w-3 h-3" /> B-Roll
                </button>
              </div>
            </section>
          ) : (
            <section className="text-center py-4 border border-dashed border-gray-300 dark:border-gray-600 rounded">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Modo simples: 1 cena com o texto fonte.
                Clique <strong>Otimizar com IA</strong> ou <button onClick={() => adicionarCena('fala')} className="text-purple-600 hover:underline inline">crie cenas manualmente</button>.
              </p>
            </section>
          )}

          {/* Salvar template */}
          {cenas.length > 0 && (
            <section className="border border-gray-200 dark:border-gray-700 rounded p-3">
              {!mostrandoSalvar ? (
                <button
                  onClick={() => setMostrandoSalvar(true)}
                  className="text-sm text-purple-700 dark:text-purple-400 hover:underline flex items-center gap-1"
                >
                  <Save className="w-4 h-4" /> Salvar layout como template
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nomeTemplate}
                    onChange={(e) => setNomeTemplate(e.target.value)}
                    placeholder="Nome do template..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded"
                  />
                  <button
                    onClick={salvarTemplate}
                    className="px-2 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded"
                  >
                    Salvar
                  </button>
                  <button
                    onClick={() => { setMostrandoSalvar(false); setNomeTemplate(''); }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    cancelar
                  </button>
                </div>
              )}
            </section>
          )}

          {erro && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{erro}</span>
            </div>
          )}

          {stats.brollPendente > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 text-xs text-amber-700 dark:text-amber-400">
              ⚠️ {stats.brollPendente} cena(s) de B-Roll com prompt mas sem imagem.
              Gere/upload/pule antes de submeter.
            </div>
          )}

          {stats.custoTotal > 1 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-xs text-yellow-700 dark:text-yellow-400">
              ⚠️ Estimativa acima de $1 USD. Considere encurtar o roteiro.
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {cenas.length > 0 ? `${cenas.length} cenas · ` : ''}
            {stats.charsFala} chars · ~{stats.duracaoSeg}s ·{' '}
            <strong className={stats.custoTotal > 1 ? 'text-yellow-700 dark:text-yellow-400' : ''}>
              ${stats.custoTotal.toFixed(4)}
            </strong>
            {stats.custoBroll > 0 && (
              <span className="opacity-70"> ({stats.custoHeygen.toFixed(4)} HG + {stats.custoBroll.toFixed(2)} B-roll)</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={gerar}
              disabled={!validoSubmit || enviando}
              className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50 flex items-center gap-1"
            >
              {enviando ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /> Gerar vídeo</>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function FonteBtn({ ativo, onClick, disabled, children }: { ativo: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1 text-xs rounded border ${
        ativo
          ? 'bg-purple-600 text-white border-purple-600'
          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
      } disabled:opacity-50`}
    >
      {children}
    </button>
  );
}
